<template>
  <!-- STEP 1: 펫 선택 -->
  <OnboardingSelect
    v-if="step === 1"
    :selected-model-index="selectedModelIndex"
    @select="onSelect"
    @next="step = 2"
    @back="onBack"
  />

  <!-- STEP 2: 메타 입력 (controlled — 상태는 이 페이지가 소유) -->
  <OnboardingMeta
    v-else
    v-model="meta"
    :selected-model-index="selectedModelIndex"
    :is-loading="isLoading"
    @complete="onComplete"
    @back="step = 1"
  />
</template>

<script>
import InMemoryStorage from "../../domain/storage/InMemoryStorage";
import SupabaseApi from "@/api/SupabaseApi";
import OnboardingSelect from "./OnboardingSelect.vue";
import OnboardingMeta from "./OnboardingMeta.vue";
import { createEmptyMeta } from "./onboarding-meta";
import { ALL_MODEL_INDEXES } from "@/constants";
import { isLocal, isDev } from "@/utils/env-utils";

const defaultModelSelect = "sloth";

export default {
  components: { OnboardingSelect, OnboardingMeta },
  created() {
    // ?step=2 로 메타 입력(STEP 2)부터 진입 가능.
    //  ⚠️ modelIndex는 쿼리로 받지 않는다 — 잠금/프리미엄 동물 임의 지정(어뷰징) 방지.
    //  동물은 STEP 1(InMemoryStorage)에서만 복원한다.
    const stored = InMemoryStorage.get("trial/selectedModelIndex");
    const wantStep2 = Number(this.$route.query.step) === 2;
    const isDevMode = isLocal() || isDev();

    if (stored) {
      this.selectedModelIndex = stored;
    } else if (isDevMode) {
      // 개발 모드: 선택값 없으면 랜덤 (STEP 2 직접 진입 테스트용)
      this.selectedModelIndex = ALL_MODEL_INDEXES[Math.floor(Math.random() * ALL_MODEL_INDEXES.length)];
    } else if (wantStep2) {
      // prod에서 뽑은 동물 없이 STEP 2 직접 진입 = 잘못된 접근 → 모델뽑기(STEP 1)로
      alert("잘못된 접근입니다. 먼저 펫을 뽑아주세요.");
      this.selectedModelIndex = defaultModelSelect;
      this.step = 1;
      return;
    } else {
      this.selectedModelIndex = defaultModelSelect;
    }

    if (wantStep2) this.step = 2;
  },
  data() {
    return {
      step: 1,
      isLoading: false,

      // 자식들의 상태를 이 페이지(부모)가 단일 소유 → 주입/복원 가능
      selectedModelIndex: null,
      meta: createEmptyMeta(),
    };
  },
  computed: {
    // ?mode=setup → 첫 계정 셋업(펫 생성 = 셋업 완료) / 없으면(또는 mode=add) 펫 추가
    isSetupMode() {
      return this.$route.query.mode === "setup";
    },
  },
  watch: {
    // step 전환 시 URL 쿼리(?step) 동기화 (이 친구로 시작=2, 뒤로=1). 새로고침/공유 시 단계 유지.
    step(n) {
      if (Number(this.$route.query.step) === n) return;
      this.$router.replace({ query: { ...this.$route.query, step: n } }).catch(() => {});
    },
  },
  mounted() {
    // 새로고침·탭 닫기 시 브라우저 네이티브 경고 (작성 중 이탈 방지)
    window.addEventListener("beforeunload", this._onBeforeUnload);
  },
  destroyed() {
    window.removeEventListener("beforeunload", this._onBeforeUnload);
  },
  // SPA 라우트 이탈 가드 — 완료/뒤로 등 의도된 이동(_allowLeave)만 통과, 그 외엔 확인.
  beforeRouteLeave(to, from, next) {
    if (this._allowLeave) return next();
    const ok = window.confirm("작성 중인 내용이 사라져요. 정말 나가시겠어요?");
    next(ok);
  },
  methods: {
    _onBeforeUnload(e) {
      e.preventDefault();
      e.returnValue = "";
    },
    onSelect(modelIndex) {
      this.selectedModelIndex = modelIndex;
    },
    onBack() {
      // 뒤로가기는 의도된 이동 → 가드 통과. 셋업: 사용자설정 / 펫추가: 펫선택
      this._allowLeave = true;
      this.$router.push(this.isSetupMode ? "/user/new" : "/pet/select");
    },
    async onComplete() {
      try {
        this.isLoading = true;

        // TODO: selectedModelIndex(짧은 이름) → model_url(버킷 키) 매핑 (온보딩 '가')
        if (this.isSetupMode) {
          // 셋업: 펫 생성 + is_complete=true → 새 펫을 활성 펫으로 선택
          const newPetId = await SupabaseApi.UserAccount.completeOnboarding(this.selectedModelIndex, this.meta);
          await SupabaseApi.UserStatus.setSelectedPet(newPetId);
          this.$store.commit("__setSelectedPetId", newPetId);

          // 스토어 하이드레이트 (App.vue 재실행 없이 홈이 바로 정상 표시되도록)
          const { name } = await SupabaseApi.fetchMyProfile();
          this.$store.commit("__setUserName", name);
          const { name: virtupetName } = await SupabaseApi.PetProfile.fetchProfile();
          this.$store.commit("__setVirtupetName", virtupetName);
          const license = await SupabaseApi.BillingPurchase.fetchLicense();
          this.$store.commit("__setExpiredAt", license.expiredAt);

          this._allowLeave = true; // 완료 = 의도된 이동, 가드 스킵
          this.$router.push("/home");
        } else {
          // 펫 추가: 펫 생성(is_complete 변경 X) → 새 펫 자동 선택 → 펫 선택 화면으로
          const newPetId = await SupabaseApi.UserAccount.createPet(this.selectedModelIndex, this.meta);
          await SupabaseApi.UserStatus.setSelectedPet(newPetId);
          this.$store.commit("__setSelectedPetId", newPetId);
          this._allowLeave = true; // 완료 = 의도된 이동, 가드 스킵
          this.$router.push("/pet/select");
        }
      } catch (error) {
        console.error(error);
        // 이름 글로벌 유일 위반(라이브 체크와의 레이스 방어) → 메타 단계로 되돌려 안내
        if (error && error.message === "E_PET_NAME_TAKEN") {
          alert("이미 사용 중인 이름이에요. 다른 이름을 입력해주세요.");
          this.step = 2;
        } else {
          alert(this.$t("onboarding.petCreateError"));
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
